import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  useSuperAppConfigWithState,
  prepareConfigForSave,
  setSuperAppConfig,
} from "./lib/superappConfig";

// Default theme fallback
const DEFAULT_THEME = {
  name: "Modern Purple",
  colors: {
    primary: "#667eea",
    secondary: "#764ba2",
    background: "#0b1221",
    text: "#ffffff",
    surface: "#1a2035",
  },
};

export const THEME_PRESETS = [
  {
    id: "purple",
    name: "Modern Purple",
    colors: {
      primary: "#667eea",
      secondary: "#764ba2",
      background: "#0b1221",
      surface: "#1a2035",
      text: "#ffffff",
      textSecondary: "rgba(255,255,255,0.7)",
    },
  },
  {
    id: "blue",
    name: "Ocean Blue",
    colors: {
      primary: "#00c6ff",
      secondary: "#0072ff",
      background: "#0f172a",
      surface: "#1e293b",
      text: "#ffffff",
      textSecondary: "rgba(255,255,255,0.7)",
    },
  },
  {
    id: "green",
    name: "Emerald",
    colors: {
      primary: "#11998e",
      secondary: "#38ef7d",
      background: "#064e3b",
      surface: "#065f46",
      text: "#ffffff",
      textSecondary: "rgba(255,255,255,0.7)",
    },
  },
  {
    id: "orange",
    name: "Sunset",
    colors: {
      primary: "#ff512f",
      secondary: "#dd2476",
      background: "#2d0f15",
      surface: "#4a1924",
      text: "#ffffff",
      textSecondary: "rgba(255,255,255,0.7)",
    },
  },
];

// Vik (Jan 14, 2026) : ThemeContext is crucial for centralized theme management.
// It provides a single source of truth for the active theme and handles the complex logic of
// syncing theme changes to the database while providing immediate feedback via optimistic UI updates.
const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  updateTheme: async (newTheme) => {},
  isSaving: false,
  error: null,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children, appId }) => {
  const { config, loading } = useSuperAppConfigWithState(appId);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize theme from config when loaded
  useEffect(() => {
    if (config?.theme) {
      setTheme(config.theme);
    }
  }, [config]);

  // Vik (Jan 23, 2026) : Apply theme variables to root
  // This injects all design system values as CSS variables so components can use them
  useEffect(() => {
    const root = document.documentElement;
    const colors = theme.colors || DEFAULT_THEME.colors;

    // Vik (Jan 23, 2026) : Core color variables (existing)
    root.style.setProperty("--primary-color", colors.primary);
    root.style.setProperty("--secondary-color", colors.secondary);
    root.style.setProperty("--bg-color", colors.background);
    root.style.setProperty("--text-color", colors.text);
    root.style.setProperty("--surface-color", colors.surface);

    // Vik (Jan 23, 2026) : Extended color variables from Design System
    if (colors.accent) root.style.setProperty("--accent-color", colors.accent);
    if (colors.success)
      root.style.setProperty("--success-color", colors.success);
    if (colors.warning)
      root.style.setProperty("--warning-color", colors.warning);
    if (colors.error) root.style.setProperty("--error-color", colors.error);
    if (colors.textSecondary)
      root.style.setProperty("--text-secondary-color", colors.textSecondary);
    if (colors.border) root.style.setProperty("--border-color", colors.border);

    // Derived variables
    root.style.setProperty("--primary-rgb", hexToRgb(colors.primary));
    root.style.setProperty("--secondary-rgb", hexToRgb(colors.secondary));

    // Vik (Jan 23, 2026) : Typography variables from Design System
    // Font families
    if (theme.typography?.fontFamilies) {
      const fonts = theme.typography.fontFamilies;
      if (fonts.primary)
        root.style.setProperty("--font-family-primary", fonts.primary);
      if (fonts.heading)
        root.style.setProperty("--font-family-heading", fonts.heading);
    }

    // Vik (Jan 23, 2026) : Typography presets - each preset becomes a set of CSS variables
    // Example: heading-1 becomes --font-heading-1-size, --font-heading-1-weight, etc.
    if (theme.typography?.presets) {
      Object.entries(theme.typography.presets).forEach(
        ([presetName, preset]) => {
          const prefix = `--font-${presetName}`;
          if (preset.fontSize)
            root.style.setProperty(`${prefix}-size`, preset.fontSize);
          if (preset.fontWeight)
            root.style.setProperty(`${prefix}-weight`, preset.fontWeight);
          if (preset.lineHeight)
            root.style.setProperty(`${prefix}-line-height`, preset.lineHeight);
          if (preset.letterSpacing)
            root.style.setProperty(
              `${prefix}-letter-spacing`,
              preset.letterSpacing,
            );
        },
      );
    }

    // Vik (Jan 23, 2026) : Spacing variables from Design System
    if (theme.spacing) {
      Object.entries(theme.spacing).forEach(([key, value]) => {
        // Convert camelCase to kebab-case for CSS variable names
        const cssKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
        root.style.setProperty(`--spacing-${cssKey}`, value);
      });
    }

    // Vik (Jan 23, 2026) : Border variables from Design System
    if (theme.borders) {
      Object.entries(theme.borders).forEach(([key, value]) => {
        const cssKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
        root.style.setProperty(`--border-${cssKey}`, value);
      });
    }
  }, [theme]);

  // Vik (Jan 14, 2026) : This function is the core of our theme governance.
  // It performs a 3-step update:
  // 1. Optimistic local update for instant UI feedback.
  // 2. Persists the change to the database to ensure it survives reloads.
  // 3. Updates the client-side cache and notifies other components.
  const updateTheme = useCallback(
    async (newTheme) => {
      if (!appId) return;

      // 1. Optimistic UI update
      setTheme(newTheme);
      setIsSaving(true);
      setError(null);

      try {
        // 2. Prepare updated config
        const currentConfig = config || { appId };
        const updatedConfig = {
          ...currentConfig,
          theme: newTheme,
        };

        // 3. Save to Database
        const prepared = prepareConfigForSave(updatedConfig);
        const res = await fetch(
          `/api/superapp/config/${encodeURIComponent(appId)}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ config: prepared }),
          },
        );

        if (!res.ok) throw new Error("Failed to save theme");

        // 4. Update Cache
        setSuperAppConfig(appId, updatedConfig);

        // 5. Notify other components
        window.dispatchEvent(
          new CustomEvent("superapp-config-updated", {
            detail: { appId },
          }),
        );
      } catch (err) {
        console.error("Failed to save theme:", err);
        setError(err.message);
        // Revert on failure (optional, but good practice)
        // setTheme(config?.theme || DEFAULT_THEME);
      } finally {
        setIsSaving(false);
      }
    },
    [appId, config],
  );

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, isSaving, error }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Helper to convert hex to rgb for rgba() usage in CSS
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
        result[3],
        16,
      )}`
    : "0, 0, 0";
}

export default ThemeContext;
